/** @jsx jsx */
import { type SerializedStyles, css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';

export type Props = {
  /**
   * ref of the target html element to move focus to.
   */
  targetRef: React.RefObject<HTMLElement>;
  /**
   * text label for the skip link
   */
  label: string;

  /**
   * The css styles to use when the skip link is visible.
   */
  visibleCss?: SerializedStyles;

  className?: string;

  /**
   * Callback for when the skip link is focused and visible
   */
  onVisible?: () => void;

  /**
   * Callback for when the skip link is blurred and hidden
   */
  onHidden?: () => void;
};

const DEFAULT_VISIBLE_CSS = css`
  position: absolute;
  right: 16px;
  top: 8px;
`;

const styles = {
  root: ({
    showSkipLink,
    visibleCss = DEFAULT_VISIBLE_CSS,
  }: {
    showSkipLink: boolean;
    visibleCss?: SerializedStyles;
  }) => {
    return showSkipLink
      ? visibleCss
      : css`
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          border: 0;
        `;
  },
};

/**
 * Reusable skip link button to programmatically focus on a DOM element of interest.
 * This is useful in scenarios where there are lots of items in a list and we need a shortcut
 * for keyboard users to skip across them all at once.
 *
 * e.g. there is a long list of items and there is a "delete all" at the bottom, this skip link
 * helps keyboard users avoid tabbing through the long list and directly go to the "delete all".
 */
export const CoachSkipLink = ({ targetRef, label, className, visibleCss, onHidden, onVisible }: Props) => {
  const [showSkipLink, setShowSkipLink] = React.useState<boolean>(false);
  const handleFocus = React.useCallback<() => void>(() => {
    setShowSkipLink(true);
    onVisible?.();
  }, [onVisible]);
  const handleBlur = React.useCallback<() => void>(() => {
    setShowSkipLink(false);

    onHidden?.();
  }, [onHidden]);
  const handleClick = React.useCallback<(e: React.MouseEvent<HTMLButtonElement>) => void>(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (targetRef.current) {
        targetRef.current.focus();
      }
    },
    [targetRef]
  );

  if (!label) {
    return null;
  }

  return (
    <CoachButton
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
      variant="ghost"
      size="small"
      className={classNames('coach-skip-link', className)}
      css={styles.root({ showSkipLink, visibleCss })}
    >
      {label}
    </CoachButton>
  );
};

export default CoachSkipLink;
